import { Page } from 'components/Page/Page';
import SummaryDetail, {
  SummaryData
} from 'components/SummaryDetail/SummaryDetail';
import {
  ChooseAgencyStrategy,
  SiteAnnouncementFormData,
  SiteAnnouncementType
} from 'core/siteAnnouncement/SiteAnnouncement';
import i18n from 'i18n';
import { Fragment, useCallback, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { SiteAnnouncementContent } from 'components/SiteAnnouncement/SiteAnnouncementContent';

export type SiteAnnouncementSummaryProps = {
  siteAnnouncement: SiteAnnouncementFormData;
  agencyOptions: SelectOptions[];
  isPublished: boolean;
  goLast: () => void;
  onSubmit: (siteAnnouncement: SiteAnnouncementFormData) => void;
  onCancel: () => void;
};

export const SiteAnnouncementSummary: React.FC<
  SiteAnnouncementSummaryProps
> = ({
  siteAnnouncement,
  agencyOptions,
  isPublished,
  goLast,
  onSubmit,
  onCancel
}) => {
  const basicSummary: SummaryData[] = useMemo(
    () => [
      {
        label: i18n.t('siteAnnouncementForm.labels.publishTime'),
        value: siteAnnouncement.publishTime
      },
      {
        label: i18n.t('siteAnnouncementForm.labels.type'),
        value: i18n.t(
          `siteAnnouncement.types.${SiteAnnouncementType[
            siteAnnouncement.type
          ].toLowerCase()}`
        )
      },
      {
        label: i18n.t('siteAnnouncementForm.labels.name'),
        value: siteAnnouncement.name
      }
    ],
    [siteAnnouncement]
  );
  const contentSummary: SummaryData[] = useMemo(
    () => [
      {
        label: i18n.t('siteAnnouncementForm.labels.imageUrl'),
        value: (() => {
          const imageSrc =
            siteAnnouncement.image?.url || siteAnnouncement.image?.imgBase64;
          return imageSrc ? (
            <img
              src={imageSrc}
              alt={i18n.t('siteAnnouncementForm.labels.imageUrl')}
              style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain' }}
            />
          ) : (
            i18n.t('common.labels.noData')
          );
        })()
      },
      {
        label: i18n.t('siteAnnouncementForm.labels.content'),
        value: (
          <SiteAnnouncementContent
            content={siteAnnouncement.content}
            macroToHyperlink={true}
          />
        )
      },
      {
        label: i18n.t('siteAnnouncementForm.labels.attachmentUrls'),
        value:
          siteAnnouncement.attachments.length > 0
            ? siteAnnouncement.attachments
                .map((attachment) => attachment.name)
                .join(', ')
            : i18n.t('common.labels.noData')
      }
    ],
    [siteAnnouncement]
  );
  const advancedSummary: SummaryData[] = useMemo(
    () => [
      {
        label: i18n.t('siteAnnouncementForm.labels.duration'),
        value:
          i18n.t('siteAnnouncementForm.labels.prefix.duration') +
          ' ' +
          siteAnnouncement.duration +
          ' ' +
          i18n.t('siteAnnouncementForm.labels.postfix.duration', {
            plural: siteAnnouncement.duration > 1 ? 's' : ''
          })
      },
      // shouldResend only applies when editing an already-published
      // announcement, mirroring the form's advanced section
      ...(isPublished
        ? [
            {
              label: i18n.t('siteAnnouncementForm.labels.shouldResend'),
              value: siteAnnouncement.shouldResend
                ? i18n.t('common.labels.yes')
                : i18n.t('common.labels.no')
            }
          ]
        : [])
    ],
    [siteAnnouncement, isPublished]
  );
  const agencySummary: SummaryData[] = useMemo(() => {
    const selectedAgencyLabels = siteAnnouncement.agencyIds.map((agencyId) => {
      const matched = agencyOptions.find(
        (option) => String(option.value) === agencyId
      );
      return matched ? matched.label : agencyId;
    });
    return [
      {
        label: i18n.t('siteAnnouncementForm.labels.chooseVendorStrategy'),
        value:
          siteAnnouncement.chooseAgencyStrategy === ChooseAgencyStrategy.ALL
            ? i18n.t('siteAnnouncementForm.labels.allAgencies')
            : `${i18n.t(
                'siteAnnouncementForm.labels.partialAgencies'
              )} ${selectedAgencyLabels.join(', ')}`
      }
    ];
  }, [siteAnnouncement, agencyOptions]);

  const handleSubmit = useCallback(() => {
    onSubmit(siteAnnouncement);
  }, [siteAnnouncement, onSubmit]);

  const renderBtns = useCallback(() => {
    return (
      <Fragment>
        <Button size='sm' onClick={handleSubmit}>
          {i18n.t('common.buttons.submit')}
        </Button>
        <Button size='sm' variant='secondary' onClick={goLast}>
          {i18n.t('common.buttons.back')}
        </Button>
        <Button size='sm' variant='secondary' onClick={onCancel}>
          {i18n.t('common.buttons.cancel')}
        </Button>
      </Fragment>
    );
  }, [handleSubmit, goLast, onCancel]);

  return (
    <Page.Content renderBtns={renderBtns}>
      <Page.Section
        title={i18n.t('siteAnnouncementForm.titles.basicSettingSection')}
      >
        <SummaryDetail data={basicSummary} />
      </Page.Section>
      <Page.Section
        title={i18n.t('siteAnnouncementForm.titles.contentSection')}
      >
        <SummaryDetail data={contentSummary} />
      </Page.Section>
      <Page.Section
        title={i18n.t('siteAnnouncementForm.titles.advancedSection')}
      >
        <SummaryDetail data={advancedSummary} />
      </Page.Section>
      <Page.Section title={i18n.t('siteAnnouncementForm.titles.agencySection')}>
        <SummaryDetail data={agencySummary} />
      </Page.Section>
    </Page.Content>
  );
};
